import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

export function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = useAuthStore((s) => s.accessToken);
  const enterLocalMode = useAuthStore((s) => s.enterLocalMode);

  const startLocal = () => {
    enterLocalMode();
    navigate('/calendar');
  };

  const features = [
    { title: t('landing.feature1Title'), desc: t('landing.feature1Desc') },
    { title: t('landing.feature2Title'), desc: t('landing.feature2Desc') },
    { title: t('landing.feature3Title'), desc: t('landing.feature3Desc') },
    { title: t('landing.feature4Title'), desc: t('landing.feature4Desc') },
  ];

  const stats = [
    { label: t('landing.statsProductLabel'), value: t('landing.statsProductValue') },
    { label: t('landing.statsModelLabel'),   value: t('landing.statsModelValue') },
    { label: t('landing.statsMvpLabel'),     value: t('landing.statsMvpValue') },
    { label: t('landing.statsPlatformLabel'),value: t('landing.statsPlatformValue') },
  ];

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <header className="mb-16">
          <div className="flex justify-between items-start mb-8">
            <div className="font-display text-2xl tracking-wide">
              Day<span className="text-daynote-accent">·</span>Note
              <span className="text-[11px] text-daynote-text/40 ml-3 tracking-widest">v1.0 / 2026</span>
            </div>
            <LanguageSwitcher />
          </div>
          <p className="font-display text-[13px] italic tracking-[0.16em] uppercase text-daynote-accent mb-4">
            {t('landing.tagline')}
          </p>
          <h1 className="font-display text-5xl md:text-[64px] leading-[1.05] font-medium tracking-tight max-w-2xl mb-6">
            {t('landing.headingLine1')}<br />
            {t('landing.headingLine2Pre')}<em className="italic text-daynote-accent font-normal">{t('landing.headingLine2Accent')}</em>{t('landing.headingLine2Post')}
          </h1>
          <p className="text-daynote-muted text-[15px] leading-relaxed max-w-xl mb-10">
            {t('landing.subtitle')}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(token ? '/calendar' : '/login')}
              className="bg-daynote-accent text-[#2a2520] px-8 py-3.5 rounded-lg font-semibold text-sm tracking-wide hover:brightness-110 transition-all border-none cursor-pointer"
            >
              {token ? t('landing.ctaOpen') : t('landing.ctaLogin')}
            </button>
            {!token && (
              <button
                type="button"
                onClick={startLocal}
                className="px-8 py-3.5 rounded-lg font-semibold text-sm tracking-wide border border-[var(--grid-line)] text-daynote-text hover:border-daynote-accent hover:text-daynote-accent transition-all bg-transparent cursor-pointer"
              >
                {t('landing.ctaLocal')}
              </button>
            )}
          </div>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-8 border-y border-[var(--grid-line)] mb-16">
          {stats.map((item) => (
            <div key={item.label}>
              <div className="text-[10px] tracking-widest uppercase text-daynote-text/40 mb-1">{item.label}</div>
              <div className="font-display text-lg">{item.value}</div>
            </div>
          ))}
        </div>

        <section>
          <h2 className="font-display text-3xl mb-3">{t('landing.featuresTitle')}</h2>
          <p className="text-sm text-daynote-muted mb-8 max-w-lg leading-relaxed">
            {t('landing.featuresSubtitle')}
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {features.map((f) => (
              <div
                key={f.title}
                className="p-6 bg-[var(--panel-bg)] backdrop-blur-xl border border-[var(--grid-line)] rounded-xl"
              >
                <h3 className="font-display text-xl mb-2">{f.title}</h3>
                <p className="text-xs text-daynote-muted leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <footer className="mt-24 pt-8 border-t border-[var(--grid-line)] text-xs text-daynote-text/40 flex justify-between">
          <span>DayNote · v1.0 · 2026-05-20</span>
          <a href="mailto:feedback@daynote.app" className="hover:text-daynote-muted">
            feedback@daynote.app
          </a>
        </footer>
      </div>
    </div>
  );
}
